import { memo, useContext, useCallback, useState, useRef, useEffect, useMemo } from 'react';

import { Text } from '@Sage/hoi-poi-ui';
import { dot } from '@Sage/ssm-utils';

import ConfirmModal from 'components/ConfirmModal';
import { getLiteral } from 'utils/getLiteral';

import { AutomationBuilderCanvasContext } from './AutomationBuilderCanvasContext';
import AutomationBuilderActionSidePanel from './sidePanelTypes/AutomationBuilderActionSidePanel';
import AutomationBuilderConditionsSidePanel from './sidePanelTypes/AutomationBuilderConditionsSidePanel';
import AutomationBuilderTriggerSidePanel from './sidePanelTypes/AutomationBuilderTriggerSidePanel';

const AutomationBuilderCanvasSidePanel = memo(({ data, onChange }) => {
    const { state, dispatch } = useContext(AutomationBuilderCanvasContext);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [closeOpen, setCloseOpen] = useState(false);
    const [isTouched, setIsTouched] = useState();
    const discardCallback = useRef();

    const onClose = useCallback(() => {
        if (state?.nextSelected) dispatch({ type: 'select', node: state.nextSelected });
        else dispatch({ type: 'close' });
        setCloseOpen(false);
        setIsTouched(false);
    }, [dispatch, state]);

    const onCheckClose = useCallback(() => {
        if (isTouched) {
            setCloseOpen(true);
        } else {
            onClose();
        }
    }, [isTouched, onClose]);

    const closeCancel = useCallback(() => {
        setCloseOpen();
    }, []);

    const openDiscard = useCallback((cb) => {
        setDiscardOpen(true);
        discardCallback.current = cb;
    }, []);

    const onInnerChange = useCallback(
        (...props) => {
            setIsTouched(true);
            onChange?.(...props);
        },
        [onChange],
    );

    const onConfirmDiscard = useCallback(() => {
        discardCallback.current?.();
        closeDiscard();
    }, [closeDiscard]);

    const closeDiscard = useCallback(() => {
        setDiscardOpen();
        discardCallback.current = null;
    }, []);

    useEffect(() => {
        if (!state.nextSelected || !state.selected) return;
        if (isTouched) {
            setCloseOpen(true);
        } else dispatch({ type: 'select', node: state.nextSelected });
    }, [dispatch, isTouched, openDiscard, state.selected, state.nextSelected, onCheckClose]);

    const sidePanel = useMemo(() => {
        if (!state.selected) return null;
        const nodePath = dot.findPathByAttr(data, 'id', state.selected.id);
        if (!nodePath) return null;
        const node = dot.get(data, nodePath);
        const baseProps = {
            data,
            node,
            nodePath,
            onClose: onCheckClose,
            onChange: onInnerChange,
            openDiscard,
            isTouched,
            setTouched: setIsTouched,
        };

        switch (node?.type) {
            case 'trigger':
                return <AutomationBuilderTriggerSidePanel key={node.id} {...baseProps} />;
            case 'action':
                return <AutomationBuilderActionSidePanel key={node.id} {...baseProps} />;
            case 'conditions':
                return <AutomationBuilderConditionsSidePanel key={node.id} {...baseProps} />;
            default:
                return null;
        }
    }, [data, isTouched, onCheckClose, onInnerChange, openDiscard, state.selected]);

    if (!state.selected) return null;

    return (
        <>
            {sidePanel}
            <ConfirmModal
                overlayClassName="fm-automation-builder__modal"
                isOpen={closeOpen}
                title={getLiteral('label_discard_unsaved_changes')}
                confirmText={getLiteral('action_discard_changes')}
                cancelText={getLiteral('action_cancel')}
                onConfirm={onClose}
                onClose={closeCancel}
            >
                <Text>{getLiteral('label_discard_unsaved_changes_desc')}</Text>
            </ConfirmModal>
            <ConfirmModal
                overlayClassName="fm-automation-builder__modal"
                isOpen={discardOpen}
                title={getLiteral('label_reset_condition_and_action')}
                confirmText={getLiteral('action_delete_all')}
                cancelText={getLiteral('action_cancel')}
                buttonType="primary-error"
                onConfirm={onConfirmDiscard}
                onClose={closeDiscard}
            >
                <Text>{getLiteral('label_reset_condition_and_action_desc')}</Text>
            </ConfirmModal>
        </>
    );
});

export default AutomationBuilderCanvasSidePanel;
